import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { FileCard } from "@/components/FileCard";
import { useAuth } from "@/context/AuthContext";
import { MicButton } from "@/components/MicButton";
import { VoiceMode } from "@/components/VoiceMode";
import { MakeDocumentButton } from "@/components/MakeDocument";
import { VideoProgress, VideoGuide } from "@/components/VideoFlow";
import { VIDEO_ACCEPT } from "@/lib/video";
import { CameraCapture } from "@/components/CameraCapture";
import { ToolsMenu } from "@/components/ToolsMenu";

function useUploads(refreshEnt) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const uploadFiles = async (list) => {
    setUploading(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await http.post("/files", fd);
        setFiles((x) => [...x, data]);
      } catch (err) {
        if (err.response?.status === 402) break;
        toast.error(`${f.name}: ${formatError(err)}`);
      }
    }
    setUploading(false);
    refreshEnt();
  };
  const upload = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    uploadFiles(list);
  };
  const remove = (id) => setFiles((x) => x.filter((y) => y.file_id !== id));
  return { files, setFiles, uploading, upload, uploadFiles, remove };
}

function AttachedFiles({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="composer-files">
      {files.map((f) => <FileCard key={f.file_id} file={f} onRemove={() => onRemove(f.file_id)} />)}
    </div>
  );
}

function SendButton({ onClick, busy, disabled, onStop }) {
  if (busy && onStop) return (
    <button onClick={onStop} data-testid="composer-stop-btn" aria-label="Stop generating"
      className="h-9 w-9 rounded-xl bg-white text-neutral-950 flex items-center justify-center hover:bg-neutral-200 transition-colors">
      <Square className="h-3.5 w-3.5 fill-current" />
    </button>
  );
  return (
    <button onClick={onClick} disabled={disabled} data-testid="send-msg-btn"
      className="h-9 w-9 rounded-xl bg-sky-400 text-white flex items-center justify-center hover:bg-sky-500 disabled:opacity-40 transition-colors">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
    </button>
  );
}

function useComposerSend({ text, setText, files, setFiles, busy, uploading, ent, onSend, taRef }) {
  return async () => {
    if (busy || uploading || (!text.trim() && !files.length)) return;
    if (ent && ent.chat_open === false && !(ent.resets_at && Date.now() >= new Date(ent.resets_at).getTime())) {
      window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "limit", resets_at: ent.resets_at } }));
      return;
    }
    const draft = { text, files };
    setText("");
    setFiles([]);
    if (taRef.current) taRef.current.style.height = "auto";
    const ok = await onSend({ text: draft.text.trim(), files: draft.files });
    if (ok === false) { setText(draft.text); setFiles(draft.files); }
  };
}

export function Composer({ onSend, onMakeDoc, onVideoFile, videoProgress = null, videoGuide = null, onCloseGuide, busy, draft, onStop }) {
  const [text, setText] = useState(draft || "");
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  useEffect(() => {
    const h = () => videoRef.current?.click();
    window.addEventListener("video-upload-pick", h);
    return () => window.removeEventListener("video-upload-pick", h);
  }, []);
  const taRef = useRef(null);
  const { refreshEnt, ent } = useAuth();
  const { files, setFiles, uploading, upload, uploadFiles, remove } = useUploads(refreshEnt);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [triggers, setTriggers] = useState({ document: 0, voice: 0, mic: 0 });
  // Welcome-screen shortcuts ("Upload homework", "Ask a question") drive the same picker and box.
  useEffect(() => {
    const up = () => fileRef.current?.click();
    const focus = () => taRef.current?.focus();
    window.addEventListener("composer-upload", up);
    window.addEventListener("composer-focus", focus);
    return () => { window.removeEventListener("composer-upload", up); window.removeEventListener("composer-focus", focus); };
  }, []);
  const pickTool = (id) => {
    if (id === "file") fileRef.current?.click();
    else if (id === "camera") setCameraOpen(true);
    else if (id === "video") videoRef.current?.click();
    else setTriggers((t) => ({ ...t, [id]: t[id] + 1 }));
  };
  useEffect(() => {
    if (!draft) return;
    setText(draft);
    window.history.replaceState({}, "");
    taRef.current?.focus();
  }, [draft]);

  const send = useComposerSend({ text, setText, files, setFiles, busy, uploading, ent, onSend, taRef });

  return (
    <div className="sticky bottom-0 backdrop-blur-xl bg-black/70 border-t border-white/10 px-2 sm:px-4 pt-3 sm:pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="max-w-3xl mx-auto">
        <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={(f) => uploadFiles([f])} />
        <VideoGuide guide={videoGuide} onClose={onCloseGuide} />
        <VideoProgress progress={videoProgress} />
        <AttachedFiles files={files} onRemove={remove} />
        <div className="flex items-end gap-1 sm:gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-2 focus-within:border-sky-400/50 transition-colors">
          <input ref={fileRef} type="file" multiple hidden onChange={upload} accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.docx,.xlsx,.xls" data-testid="file-input" />
          <input ref={videoRef} type="file" hidden accept={VIDEO_ACCEPT} data-testid="video-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onVideoFile(f); }} />
          <ToolsMenu onPick={pickTool} disabled={busy} uploading={uploading || videoProgress !== null} />
          <MakeDocumentButton hideButton trigger={triggers.document} onMake={onMakeDoc} />
          <VoiceMode hideWhenIdle trigger={triggers.voice} disabled={busy} onSend={onSend} />
          <MicButton hideWhenIdle trigger={triggers.mic} disabled={busy} onText={(t) => { setText((x) => (x ? `${x} ${t}` : t)); taRef.current?.focus(); }} />
          <textarea ref={taRef} rows={1} value={text} data-testid="chat-input"
            placeholder="Ask anything"
            onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`; }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 min-w-0 resize-none bg-transparent outline-none text-base sm:text-[15px] py-2 px-1 placeholder:text-neutral-500 max-h-[200px]" />
          <SendButton onClick={send} busy={busy} disabled={busy || uploading} onStop={onStop} />
        </div>
        <p className="hidden sm:block text-[11px] text-neutral-600 mt-2 px-1">BrynAI can make mistakes. Check important answers.</p>
      </div>
    </div>
  );
}
